import { createEffect, createStore } from 'effector'
import { type ApiResponseError } from '../../interfaces/errors'
import { stateful } from '../../lib/stateful'
import {
  type Content,
  type ContentGenresToLanguages,
  type ContentLanguage,
  type ContentTag,
} from './index.h'

export {
  type Content,
  type ContentGenresToLanguages,
  type ContentLanguage,
  type ContentTag,
}

export interface ContentList extends Array<Content> {
  total: number
}

type PageParams = { limit?: number; offset?: number }
type SortParams = { by?: string; order?: string }
type SearchParams = { q?: string }
type FilterParams = SearchParams & {
  creatorId?: number
}
type OldFilterParams = SearchParams & {
  tagIds?: number[]
  collectionIds?: number[]
  creatorId?: number
}
type GetByGenreId = { genreId?: number }
type GetByCollectionId = { collectionId?: number }
type GetByLanguageId = { languageIdQueryParam?: number }
type GetByIdParams = { getByTag?: boolean; id: number }
type GetManyParams = GetByIdParams & PageParams & SortParams & FilterParams
type GetManyOldParams = PageParams & SortParams & OldFilterParams
type GetManyContentParams = GetByGenreId &
  GetByCollectionId &
  PageParams &
  GetByLanguageId
type GetContentSetVodParams = { id: number } & PageParams

type RequestHeaders = { headers: Headers }
type GetOneParams = { id: number }
type FavoriteParams = { id: number }
type WithDescriptions = { withDescriptions?: 0 | 1 }

// *
// * base API effects
// *

export const base = {
  getManyOldFx: createEffect<GetManyOldParams, Content[]>(),
  getManyFx: createEffect<GetManyParams, ContentList>(),
  getContentFx: createEffect<GetManyContentParams, ContentList>(),
  getGenresToLanguagesFx: createEffect<void, ContentGenresToLanguages>(),
  getRecommendedFx: createEffect<
    PageParams & SortParams & SearchParams,
    ContentList
  >(),
  getFavoritesFx: createEffect<PageParams, ContentList>(),
  addToFavoritesFx: createEffect<FavoriteParams, void>(),
  removeFromFavoritesFx: createEffect<FavoriteParams, void>(),
  getOneFx: createEffect<GetOneParams & RequestHeaders, Content>(),
  getContentGenresFx: createEffect<WithDescriptions, ContentTag[]>(),
  getContentLanguagesFx: createEffect<WithDescriptions, ContentLanguage[]>(),
  getContentSetVodFx: createEffect<GetContentSetVodParams, ContentList>(), //TODO: contentSetVod?? TYPES
  getOneContentSetFx: createEffect<GetOneParams & RequestHeaders, Content>(),
}

// *
// *
// *

// GET /api/web/content/by-tag/{id} (by default), /api/web/content/by-collection/{id}
export const pageable = (initial?: GetManyParams) =>
  stateful({
    effect: base.getManyFx,
    initial,
    methods: {
      limit: (n: number, params) => ({ ...params, limit: n }),
      offset: (n: number, params) => ({ ...params, offset: n }),
      next: (_: void, params) => ({
        ...params,
        offset: (params.offset ?? 0) + (params.limit ?? 36),
      }),
      prev: (_: void, params) => ({
        ...params,
        offset: Math.max((params.offset ?? 0) - (params.limit ?? 36), 0),
      }),
      sort: ({ by, order }: SortParams, params) => ({ ...params, by, order }),
      by: (by: string | undefined, params) => ({ ...params, by }),
      order: (order: string | undefined, params) => ({ ...params, order }),
      filter: (q: string | undefined, params) => ({ ...params, q }),
    },
  })

export const pageableContent = (initial?: GetManyContentParams) =>
  stateful({
    effect: base.getContentFx,
    initial,
    methods: {
      limit: (n: number, params) => ({ ...params, limit: n }),
      offset: (n: number, params) => ({ ...params, offset: n }),
      next: (_: void, params) => ({
        ...params,
        offset: (params.offset ?? 0) + (params.limit ?? 36),
      }),
      prev: (_: void, params) => ({
        ...params,
        offset: Math.max((params.offset ?? 0) - (params.limit ?? 36), 0),
      }),
    },
  })

export const pageableFavorites = (initial?: PageParams) =>
  stateful({
    effect: base.getFavoritesFx,
    initial,
    methods: {
      limit: (n: number) => ({ limit: n }),
      offset: (n: number) => ({ offset: n }),
      /* The logic for changing offset has been moved to the effector model
      because offset should change when items are removed from favorites */
      next: (_: void) => ({}),
      prev: (_: void, { offset = 0, limit = 36 }) => ({
        offset: Math.max(offset - limit, 0),
      }),
    },
  })

export const pageableRecommended = (
  initial?: PageParams & SortParams & SearchParams
) =>
  stateful({
    effect: base.getRecommendedFx,
    initial,
    methods: {
      limit: (n: number, params) => ({ ...params, limit: n }),
      offset: (n: number, params) => ({ ...params, offset: n }),
      next: (_: void, params) => ({
        ...params,
        offset: (params.offset ?? 0) + (params.limit ?? 36),
      }),
      prev: (_: void, params) => ({
        ...params,
        offset: Math.max((params.offset ?? 0) - (params.limit ?? 36), 0),
      }),
    },
  })

export const pageableContentSet = (initial?: GetContentSetVodParams) =>
  stateful({
    effect: base.getContentSetVodFx,
    initial,
    methods: {
      limit: (n: number, params) => ({ ...params, limit: n }),
      offset: (n: number, params) => ({ ...params, offset: n }),
      next: (_: void, params) => ({
        ...params,
        offset: (params.offset ?? 0) + (params.limit ?? 36),
      }),
      prev: (_: void, params) => ({
        ...params,
        offset: Math.max((params.offset ?? 0) - (params.limit ?? 36), 0),
      }),
    },
  })

// Errors

export const error = {
  getOneFx: createStore<null | ApiResponseError>(null).on(
    base.getOneFx.fail,
    (_, { error }) => {
      // @ts-ignore because error does not exist in type Error
      const response = error?.error?.response
      if (!response) return

      return {
        code: response.status,
        message: response.statusText,
      }
    }
  ),
}
